import {
  TaskCardView,
  TaskCardViewProps,
} from '@euler/app/tv/components/dashboard/TaskCardView';
import {
  TaskListMode,
  TaskPack,
} from '@euler/app/tv/functions/useDashboardTaskListMetrics';
import { getTaskStatusInfoFromTask } from '@euler/model';
import { VehicleInspectionTask } from '@euler/model/entity';
import { memo, useMemo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';

export const TaskPackCell = memo(
  ({
    pack,
    margin,
    style,
    ...props
  }: {
    mode: TaskListMode;
    pack: TaskPack<VehicleInspectionTask>;
    width: number;
    height: number;
    margin: number;
    showFinishedTime?: boolean;
    showStatus?: boolean;
    style?: StyleProp<ViewStyle>;
    onTaskPress?: (task: VehicleInspectionTask) => void;
  }) => {
    if (pack.capacity === 1) {
      return (
        <TaskCell
          task={pack.tasks[0]}
          rowIndex={pack.rowIndex}
          colIndex={pack.colIndex}
          {...props}
          css={`
            margin-right: ${margin}px;
            margin-bottom: ${margin}px;
            width: ${props.width}px;
            height: ${props.height}px;
          `}
          style={style}
        />
      );
    }
    return (
      <View
        css={`
          margin-right: ${margin}px;
          margin-bottom: ${margin}px;
          height: ${(props.height + margin) * pack.capacity - margin}px;
        `}
        style={style}
      >
        {pack.tasks.map((task, i) => (
          <TaskCell
            key={task.taskNo}
            task={task}
            rowIndex={pack.rowIndex}
            colIndex={pack.colIndex}
            {...props}
            css={`
              width: ${props.width}px;
              height: ${props.height}px;
              margin-bottom: ${i < pack.capacity - 1 ? margin : 0}px;
            `}
          />
        ))}
      </View>
    );
  },
);

const TaskCell = memo(
  ({
    mode,
    task,
    rowIndex,
    colIndex,
    width,
    height,
    showFinishedTime,
    showStatus,
    style,
    onTaskPress,
  }: {
    mode: TaskListMode;
    task: VehicleInspectionTask;
    rowIndex: number;
    colIndex: number;
    width: number;
    height: number;
    showFinishedTime?: boolean;
    showStatus?: boolean;
    style?: StyleProp<ViewStyle>;
    onTaskPress?: (task: VehicleInspectionTask) => void;
  }) => {
    const statusInfo = useMemo(() => getTaskStatusInfoFromTask(task), [task]);
    const props: TaskCardViewProps = {
      task,
      taskNo: task.taskNo,
      compact: mode === 'vertical-compact',
      rowIndex,
      colIndex,
      width,
      height,
      horizontal: mode === 'horizontal-complete',
      vehicleName: task.vehicleName,
      licensePlateNo: task.licensePlateNo,
      serviceAgentName: task.serviceAgentName,
      mileage: task.vehicleMileage,
      hasObserved: task.observes != null && task.observes?.length > 0,
      score:
        task.score ?? task.inspectionScore ?? task.preInspectionScore ?? 100,
      brandLogoUrl: task.vehicleBrandLogo ?? undefined,
      vehicleImageUrl: task.vehicleImageUrl
        ? `${task.vehicleImageUrl}#${encodeURIComponent(
            task.vehicleImageBlurhash ?? '',
          )}`
        : undefined,
      createdAt: task.createdAt,
      updatedAt: task.updatedAt ?? undefined,
      finishedAt: task.finishedAt ?? undefined,
      showFinishedTime,
      showStatus,
      statusInfo,
    };

    return <TaskCardView {...props} style={style} onPress={onTaskPress} />;
  },
);
